/* @flow */
import { Map, Set, List, fromJS, OrderedMap } from 'immutable';
import { combineReducers } from 'redux-immutable';
import { extent } from 'd3-array';
import { pipe, equals } from 'ramda';
import compareDesc from 'date-fns/compare_desc';
import minDate from 'date-fns/min';
import type { Action } from 'trustar/utils/redux-types.js';
import { pick } from 'trustar/utils/utils.js';
import {
  Graph,
  CurrentGraph,
  GraphOptions,
  GraphMeta,
  toGraphData,
  toNodePositions,
  getNodeTime,
  getNodeTimes,
  type RawGraph,
} from 'trustar/constellation/graph-utils.js';
import { IO } from 'trustar/fp';
import createDebugger from 'debug';

const debug = createDebugger('app:modules:constellation:stores'); // eslint-disable-line no-unused-vars

import {
  FETCH,
  FETCH_SUCCESS,
  FETCH_FAILURE,
  FETCH_EXPLOSION,
  FETCH_EXPLOSION_SUCCESS,
  CLEAR_EXPLOSION,
  CACHE_GRAPH_SIMULATION_SUCCESS,
  INVALIDATE_GRAPH_CACHE,
  CLEAR,
  TOGGLE_NODE,
  DELETE_NODES_SUCCESS,
  REPLACE_SELECTION,
  SET_FILTERS_SUCCESS,
  SET_OPTIONS,
  SET_HOVERED_NODE_ID,
  SET_ROOT_NODE_ID,
  UNDO_SUCCESS,
  REDO_SUCCESS,
  RESET_SUCCESS,
  UPDATE_TIME_RANGE,
  CLOSE_TIME_RANGE_EXPLANATION,
  SAVE_NOTE_SUCCESS,
  UPDATE_NOTE_SUCCESS,
  DELETE_NOTE_SUCCESS,
  PROCESS_ANNOTATIONS_SUCCESS,
  SET_ACTIVE_NOTE,
  RESET_HIGHLIGHT_NODE,
  HIGHLIGHT_NODE,
  ADD_BREADCRUMB,
  GO_TO_BREADCRUMB,
  RESET_BREADCRUMBS
} from './action-types.js';
import {
  FETCH_ANALYSIS_SUCCESS,
  FETCH_ANALYSIS_FAILURE,
  DELETE_ANALYSIS_SUCCESS
} from 'modules/analysis/action-types.js';
import {
  REMOVE as REMOVE_REPORT,
  REMOVE_SUCCESS as REMOVE_REPORT_SUCCESS
} from 'modules/reports/action-types.js';
import {
  SET_LAST_ROUTE
} from 'modules/app/action-types.js';
import { UPDATE_REPORT_SUCCESS } from 'modules/create/action-types';

import * as CompanyWhitelist from 'modules/company-whitelist';
import type {BreadcrumbRecordType} from './types';

/* The third argument to pipe is to protect against d3 extent function default for
 * empty array which returns undefined: to avoid we return the default state [0, 0] */
const getNodeTimeExtent = pipe(getNodeTimes, extent, (([a, b]) => [a || 0, b || 0]));

/* Stores
 *
 * NOTE: For most merge actions we merge deep because new data can continously
 * come into the cache and be stored on a node. For example, when we save the
 * x,y positions of nodes we don't want those to get wiped away by doing a
 * shallow merge. By doing a deep merge we allow the data stored here to be
 * built up over time.
 * ======================================================================= */

/**
 * We keep track of the ids of the last exploded nodes so that we can do certain
 * things with them but not with any other nodes in the graph. There may be a
 * better way to identify which nodes just exploded into the graph other than
 * setting their ids here.
 */
const explosionIds = (state = Set(), action: Action<{ data: RawGraph }>): Set<string> => {
  const { type, payload } = action;

  switch (type) {
  case FETCH_EXPLOSION_SUCCESS:
    return Set(payload.data.nodes.map(x => x.id));
  case FETCH_SUCCESS:
  case CLEAR_EXPLOSION:
    return Set(); // Reset
  default:
    return state;
  }
};

const rootNodeId = (state = null, action): ?string => {
  const { type, payload } = action;

  switch (type) {
  case FETCH_SUCCESS:
    return payload.id;
  case SET_ROOT_NODE_ID:
    return payload;
  case FETCH:
  case CLEAR:
  case FETCH_FAILURE:
    return null;
  default:
    return state;
  }
};

/**
 * We use this for adding a tooltip to the node that is hovered in the UI. Can
 * be null when no node is hovered. Also, reset to null after exploding a node.
 */
const hoveredNodeId = (state = null, action) => {
  const { type, payload } = action;

  switch (type) {
  case SET_HOVERED_NODE_ID:
    return payload;
  case FETCH_EXPLOSION_SUCCESS:
    return null;
  default:
    return state;
  }
};

const updateSetIfNeeded = (state: Set<string>, list: string[]): Set<string> => {
  if (state.size === list.length && list.every(x => state.has(x))) {
    return state;
  } else {
    return Set(list);
  }
};

/**
 * NOTE: We set the current node immediately after fetch if it is found so that
 * when the user first opens correlation the current report is the selected
 * node.
 * Also, we try the node id and initial id for a match becuase if this is an
 * indicator node then there is no initial id.
 */
const currentNodeIds = (state = Set(), action) => {
  const { type, payload } = action;

  switch (type) {
  case FETCH_SUCCESS:
    const node = payload.data.nodes.find(x =>
      (x.id === payload.id) || (x.initialId === payload.id)); // See NOTE
    return node ? Set.of(node.id) : state;
  case FETCH_ANALYSIS_SUCCESS:
    return payload.data.get('nodes').filter(node => node.get('rootNode')).map(n => n.get('id')).toSet();
  case TOGGLE_NODE:
    return state.has(payload) ? state.remove(payload) : state.add(payload);
  case REPLACE_SELECTION:
    return updateSetIfNeeded(state, payload);
  case CompanyWhitelist.actionTypes.SAVE_WHITELIST_ENTRY_SUCCESS:
      return state.filter(x => x === payload.get('guid')).size === 0 ? state : Set();
  case FETCH:
  case DELETE_NODES_SUCCESS:
  case REMOVE_REPORT:
  case SET_LAST_ROUTE:
    return Set();
  case FETCH_EXPLOSION:
    return Set.of(payload.id);
  default:
    return state;
  }
};

const currentGraphId = (state = null, action: Action<{ id: string }>) => {
  const { type, payload } = action;

  switch (type) {
  case FETCH_ANALYSIS_SUCCESS:
  case FETCH_SUCCESS:
  case FETCH_FAILURE:
    return payload.id;
  case UPDATE_REPORT_SUCCESS: // Constellation will be refetched, delete the current data
  case DELETE_ANALYSIS_SUCCESS:
  case REMOVE_REPORT_SUCCESS:
  case FETCH_ANALYSIS_FAILURE:
  case CLEAR:
    return null;
  default:
    return state;
  }
};

const options = (state: typeof GraphOptions = GraphOptions(), action) => {
  const { type, payload } = action;

  switch (type) {
  case SET_OPTIONS:
    return state.merge(payload);
  default:
    return state;
  }
};

const fromAnalysis = pick('nodes', 'edges');

const isRootNode = (node: Map<string, *>) => node.get('rootNode', false);

/**
 * NOTE: We make an assumption that any graph has a single root node. If it has
 * multiple we should still be fine, since `find` will just take the first one
 * (although there should never be more than one). However, if for some reason
 * we do NOT get a rootNode in the graph this will be innaccurate. The IO should
 * keep anything from breaking, but the data will be off.
 */
export const getInitialTimeRange = (nodes: OrderedMap<string, *>, limit?: number) => {
  const rootNodeTime = IO(() => nodes)
    .map(nodes => nodes.find(isRootNode)) // See NOTE
    .map(getNodeTime)
    .fold(err => {
      debug('Warning: There was an error getting the rootNodeTime of this graph', err);
      return Date.now(); // Default to now, so it won't affect our comparison
    }, time => {
      return time || Date.now();
    });

  const limitedNodes = limit
  ? nodes.valueSeq()
    .filter(getNodeTime) // Only account for nodes with a time
    .sort((a, b) => compareDesc(getNodeTime(a), getNodeTime(b)))
    .take(limit)
  : nodes.valueSeq().filter(getNodeTime);

  const [ min, max ] = getNodeTimeExtent(limitedNodes);

  return [
    minDate(min, rootNodeTime).getTime(), // Ensure that the root node
    max
  ];
};

const toAnalysisData = pipe(
  fromAnalysis,
  x => x.toJS(), // Don't remember why we do this. Looks like we use fromAnalysis just to normalize shape and then reconvert to plain object
  toGraphData,
);

/* Handle various actions that need to update state history for UNDO/REDO */
const updateGraphHistory = (state) => {
  const position = state.getIn(['history', 'index']);
  const timeline = state.getIn(['history', 'timeline']);
  if (position < timeline.size) {
    return state
     .updateIn(['history', 'index'], i => i + 1)
     .setIn(['history', 'timeline'], timeline.take(position).push(state.get('currentGraph')));
  } else {
    return state
     .updateIn(['history', 'index'], i => i + 1)
     .updateIn(['history', 'timeline'], t => t.push(state.get('currentGraph')));
  }
};

/**
 * The graph reducer updates individual graphs within the larger OrderedMap. It
 * does much of the heavy lifting of maintaining graph state.
 *
 * NOTE: We decided that when a user explodes a node we want to show all the
 * nodes that were returned even if they were previously deleted. So we remove
 * their id from the deleted nodes list.
 */

const graph = (state: typeof Graph = Graph(), action) => {
  const { type, payload } = action;

  switch (type) {

  // Remove the cached graph state as well as undo history, however, LEAVE
  // INTACT the node positioning cache.
  case INVALIDATE_GRAPH_CACHE:
    return state
      .remove('currentGraph')
      .remove('history');

  case FETCH_ANALYSIS_SUCCESS: {
    const data = toAnalysisData(payload.data);
    let timeRange = state.getIn(['currentGraph', 'timeRange']);

    // For analysis we default the timeRange to the full extent rather than only
    // showing a subset as we do for normal graphs
    if (equals(timeRange, CurrentGraph().timeRange)) {
      timeRange = getNodeTimeExtent(data.nodes);
    }

    return state
      .setIn(['currentGraph', 'id'], payload.id)
      .setIn(['currentGraph', 'type'], 'analysis')
      .mergeDeepIn(['currentGraph', 'data'], data)
      .setIn(['currentGraph', 'timeRange'], timeRange); // Set timeRange based on nodes
  }

  case FETCH_SUCCESS: {
    const data = toGraphData(payload.data);
    let timeRange = state.getIn(['currentGraph', 'timeRange']);

    // Default the initial extent to the extent of the most recent 30 nodes
    // This is meant to alleviate really large graphs by only showing a subset
    // initially
    if (equals(timeRange, CurrentGraph().timeRange)) {
      timeRange = getInitialTimeRange(data.get('nodes'), 30);
    }

    return state
      .setIn(['currentGraph', 'id'], payload.id)
      .setIn(['currentGraph', 'viewType'], 'report')
      .setIn(['currentGraph', 'type'], payload.type)
      .setIn(['currentGraph', 'indicatorType'], payload.indicatorType)
      .setIn(['currentGraph', 'value'], payload.value)
      .mergeDeepIn(['currentGraph', 'data'], data) // See NOTE
      .setIn(['currentGraph', 'timeRange'], timeRange) // Set timeRange based on nodes
      .setIn(['currentGraph', 'initialSettledState'], null) // Reset initial settled state
      .setIn(['history', 'index'], 0)
      .setIn(['history', 'timeline'], List())
      .updateIn(['currentGraph', 'filters'], f => f.clear());
  }

  case SET_FILTERS_SUCCESS:
    return updateGraphHistory(state).mergeIn(['currentGraph', 'filters'], payload.filters);

  case DELETE_NODES_SUCCESS:
    return updateGraphHistory(state).mergeIn(['currentGraph', 'deletedNodes'], payload.data);

  case FETCH_EXPLOSION_SUCCESS: {
    // We set root node flag to false on all nodes since this is an explosion.
    // The server doesn't know since we issued a standard request, but we need
    // to keep track of the fact that none of these nodes is actually the root
    // of the graph we will be displaying.
    const data = toGraphData({
      nodes: payload.data.nodes.map(x => {
        x.rootNode = false;
        x.isExplosion = true;
        return x;
      }),
      edges: payload.data.edges,
    });

    const merged = state.getIn(['currentGraph', 'data', 'nodes']).merge(data.get('nodes'));

    // Compute the maximum time range for every node expansion, otherwise not
    // all the new nodes in the expansion may appear due to the initial time
    // range possibly filtering these nodes out.
    const timeRange = getInitialTimeRange(merged);

    return updateGraphHistory(state)
    .mergeDeepIn(['currentGraph', 'data'], data)
    .setIn(['currentGraph', 'timeRange'], timeRange); // Set timeRange based on nodes
  }

  case CACHE_GRAPH_SIMULATION_SUCCESS: {
    const meta = GraphMeta({
      id: payload.id,
      nodePositions: toNodePositions(payload.data),
      initialSettledState: null,
    });
    return state
      .updateIn(['currentGraph', 'initialSettledState'], x => x || state) // Push initial settled state only once
      .set('meta', meta);
  }

  case UNDO_SUCCESS: {
    const position = state.getIn(['history', 'index']);
    if (position > 0) {
      const previousState = state.getIn(['history', 'timeline']).get(position - 1);
      /* if we undo at the end of the timeline we need to add the current state to the timeline
         to history so if we redo we can revisit it: */
      if (position === state.getIn(['history', 'timeline']).size) {
        const currentState = state.get('currentGraph');
        return state
          .updateIn(['history', 'timeline'], t => t.push(currentState))
          .updateIn(['history', 'index'], i => i - 1)
          .set('currentGraph', previousState);
      } else {
        return state
          .updateIn(['history', 'index'], i => i - 1)
          .set('currentGraph', previousState);
      }
    } else {
      return state;
    }
  }

  case REDO_SUCCESS: {
    const position = state.getIn(['history', 'index']);
    if (position < state.getIn(['history', 'timeline']).size - 1) {
      const nextState = state.getIn(['history', 'timeline', position + 1]);
      return state
        .set('currentGraph', nextState)
        .updateIn(['history', 'index'], i => i + 1);
    } else {
      return state;
    }
  }

  case RESET_SUCCESS: {
    if (state.getIn(['history', 'timeline']).size === 0) return state; // default if no actions are taken yet
    const initialState = state.getIn(['currentGraph', 'initialSettledState'], state);
    if (!initialState) return state; // safety catch
    return initialState
      .setIn(['history', 'index'], 0)
      .setIn(['history', 'timeline'], List())
      .updateIn(['currentGraph', 'filters'], f => f.clear());
  }

  case UPDATE_TIME_RANGE:
    return state.setIn(['currentGraph', 'timeRange'], payload.data);

  default:
    return state;
  }
};

/**
 * NOTE: Currently this does nothing other than delegate updates to the graph
 * function based on the passed id. There's likely a more elegant way to do
 * this since it's not doing much. We could just update state no matter what if
 * there is an id in the payload...
 */
const graphs = (state = Map(), action: Action<any>): Map<string, typeof Graph> => {
  const { type, payload } = action;

  switch (type) {
  case FETCH_ANALYSIS_SUCCESS:
  case FETCH_SUCCESS:
  case FETCH_EXPLOSION_SUCCESS:
  case SET_FILTERS_SUCCESS:
  case DELETE_NODES_SUCCESS:
  case CACHE_GRAPH_SIMULATION_SUCCESS:
  case UNDO_SUCCESS:
  case REDO_SUCCESS:
  case UPDATE_TIME_RANGE:
  case RESET_SUCCESS:
    return state.update(payload.id, (x) => graph(x, action));

  case INVALIDATE_GRAPH_CACHE: // Take that cache (╯°□°）╯︵ƎHƆ∀Ɔ
    return state.map(x => graph(x, action)); // Run on all graphs

  case UPDATE_REPORT_SUCCESS:
    return state.delete(payload); // Constellation will be refetched, delete the current data

  default:
    return state;
  }
};

const loading = (state = false, action) => {
  const { type } = action;

  switch (type) {
  case FETCH:
    return true;
  case FETCH_SUCCESS:
  case FETCH_FAILURE:
    return false;
  default:
    return state;
  }
};

const hasUserSeenTimeRangeExplanation = (state = false, action) => {
  switch (action.type) {
  case CLOSE_TIME_RANGE_EXPLANATION:
    return true;
  default:
    return state;
  }
};

const createOrUpdateNoteList = note => list => {
  const result = list || OrderedMap();
  return result.set(note.guid, note);
};

/**
 * A map of node ids to their associated ordered maps of notes
 *
 * NOTE: This could be considered a "thin" store / reducer, because it really
 * doesn't do much. The majority of the data processing has been off-loaded to
 * the epics since there is a lot of data mapping giong on and epics can handle
 * errors cracefully.
 */
const notes = (state = Map(), action) => {
  const { type, payload } = action;

  switch (type) {
  case SAVE_NOTE_SUCCESS:
  case UPDATE_NOTE_SUCCESS:
    return state.update(payload.nodeGuid, createOrUpdateNoteList(payload));
  case PROCESS_ANNOTATIONS_SUCCESS:
    return state.merge(payload);
  case DELETE_NOTE_SUCCESS:
    return state
      .removeIn([payload.nodeGuid, payload.guid])
      .filter(x => !x.isEmpty()); // Remove empty maps. I.e. if they have no notes
  default:
    return state;
  }
};

/**
 * NOTE: It's highly likely a user would save a note after deep linking in to
 * see one in which they were tagged. If the active note is NOT reset then after
 * saving a note the note list will still scroll back to the "active" note,
 * which makes no sense. We want to scroll to the bottom, so we reset the active
 * note.
 */
const activeNote = (state = null, action) => {
  const { type, payload } = action;

  switch (type) {
  case SET_ACTIVE_NOTE:
    return payload;
  case SAVE_NOTE_SUCCESS: // See NOTE
    return null;
  default:
    return state;
  }
};

export const constellation = combineReducers({
  graphs,
  currentGraphId,
  rootNodeId,
  hasUserSeenTimeRangeExplanation,
  notes,
});

/**
 * Used to highlight a node on the graph in whichever way you want
 * @param state, a node
 * @param action
 * @returns {<*, *>}
 */
const highlightedNode = (state: Map<any, any> = Map(), action: Action<string>) => {
  const { type, payload } = action;

  switch(type) {
  case HIGHLIGHT_NODE:
    return payload;
  case RESET_HIGHLIGHT_NODE:
    return Map();
  default:
    return state;
  }
};

const bringToTopOrAddBreadcrumb = (state, payload) => {
  if (state.has(payload.id)) {
    return state.remove(payload.id).set(payload.id, fromJS(payload));
  } else {
    return state.set(payload.id, fromJS(payload));
  }
}
const breadcrumbTrail = (state: OrderedMap<any, BreadcrumbRecordType> = OrderedMap(), action: Action<any>) => {
  const { type, payload } = action;

  switch(type) {
  case RESET_BREADCRUMBS:
    return state.clear();
  case ADD_BREADCRUMB:
    return bringToTopOrAddBreadcrumb(state, payload);
  case GO_TO_BREADCRUMB:
  default:
    return state;
  }
};
const currentBreadcrumbId = (state: string = '', action: Action<any>) => {
  const { type, payload } = action;
  switch(type) {
    case RESET_BREADCRUMBS:
      return '';
    case ADD_BREADCRUMB:
      return payload.id;
    case GO_TO_BREADCRUMB:
      return payload;
    default:
      return state;
  }
};
/**
 * NOTE: These reducers were initially factored out of the constellation store
 * for performance reasons. Updates to unrelated fields were causing the rather
 * expensive currentGraphData getter to be called A LOT.
 */
export const constellationMeta = combineReducers({
  explosionIds,
  hoveredNodeId,
  currentNodeIds, // The set of currently selected nodes
  options,
  loading,
  activeNote,
  highlightedNode,
  breadcrumbTrail,
  currentBreadcrumbId,
});



// WEBPACK FOOTER //
// ./src/app/modules/constellation/stores.js